import { useEffect, useState } from "react";
import { Check, Pause, Play, RotateCcw, Send } from "lucide-react";

const FieldGroup = ({ fields, value = {}, onChange }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    {fields.map((label, index) => (
      <label key={label} className="block text-sm font-semibold">
        {label}
        <textarea value={value[index] || ""} onChange={(event) => onChange(index, event.target.value)} rows={2}
          className="mt-1 w-full rounded-xl border-2 border-[#eadfce] bg-white px-3 py-2 font-normal focus:border-[var(--ink)] focus:outline-none" />
      </label>
    ))}
  </div>
);

export default function DayActivity({ day, notes, update, ticks, onTick }) {
  const [seconds, setSeconds] = useState(60);
  const [running, setRunning] = useState(false);
  const activity = day.activity;
  const fieldsValue = notes.activityFields || {};
  const setField = (index, value) => update({ activityFields: { ...fieldsValue, [index]: value } });
  const scenario = day.lesson.scenario;
  const scenarioChoice = Number.isInteger(notes.scenarioChoice) ? notes.scenarioChoice : null;

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => setSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  useEffect(() => {
    if (seconds === 0) setRunning(false);
  }, [seconds]);

  const renderFields = (fields) => <FieldGroup fields={fields} value={fieldsValue} onChange={setField} />;

  return (
    <section className="rounded-2xl bg-[var(--cream)] p-4 sm:p-5" data-testid="day-activity">
      <p className="text-xs font-semibold uppercase text-[var(--sage-deep)]">Práctica del día</p>
      <h3 className="font-display text-xl mt-1">{activity.title}</h3>
      <p className="mt-2 text-sm text-[#5a4f46]">{activity.prompt}</p>

      {activity.mode === "case" && (
        <div className="mt-4" data-testid="day-scenario">
          <p className="font-semibold">{scenario.situation}</p>
          <div className="mt-3 space-y-2" role="group" aria-label="Elige una respuesta para el caso práctico">
            {scenario.choices.map((choice, index) => (
              <button key={choice.text} type="button" data-testid={`scenario-choice-${index}`} aria-pressed={scenarioChoice === index}
                onClick={() => update({ scenarioChoice: index })}
                className={`w-full rounded-xl border-2 p-3 text-left text-sm transition-colors ${scenarioChoice === index ? "border-[var(--sage-deep)] bg-white" : "border-[#eadfce] bg-transparent hover:bg-white"}`}>
                {choice.text}
              </button>
            ))}
          </div>
          {scenarioChoice !== null && <p role="status" data-testid="scenario-feedback" className="mt-3 rounded-xl border-l-4 border-[var(--sage-deep)] bg-white p-3 text-sm text-[#4a4038]">{scenario.choices[scenarioChoice].feedback}</p>}
        </div>
      )}

      {activity.mode === "pause" && (
        <div className="mt-4 rounded-xl bg-white p-4" data-testid="pause-timer">
          <p className="font-display text-3xl tabular-nums" aria-live="polite">{`0:${String(seconds).padStart(2, "0")}`}</p>
          <p className="mt-1 text-sm text-[#5a4f46]">Respira con comodidad; puedes parar en cualquier momento.</p>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => setRunning((value) => !value)} className="btn-ghost !py-2 text-sm">{running ? <Pause size={16} /> : <Play size={16} />}{running ? "Pausar" : seconds === 0 ? "Repetir" : "Empezar"}</button>
            <button type="button" onClick={() => { setRunning(false); setSeconds(60); }} aria-label="Reiniciar temporizador" className="btn-ghost !py-2"><RotateCcw size={16} /></button>
          </div>
        </div>
      )}

      {activity.mode === "write" && (
        <textarea data-testid="activity-response" value={notes.activityText || ""} onChange={(event) => update({ activityText: event.target.value })} rows={4}
          placeholder="Escribe una idea breve para probar…" className="mt-4 w-full rounded-xl border-2 border-[#eadfce] bg-white px-3 py-2 focus:border-[var(--ink)] focus:outline-none" />
      )}

      {["fields", "observe", "review", "map", "rewrite", "pause-plan", "agreement"].includes(activity.mode) && (
        <div className="mt-4">{renderFields(activity.fields || ["Qué observé", "Qué necesito", "Qué quiero probar"])}</div>
      )}

      {activity.mode === "choice" && (
        <div className="mt-4 space-y-2" role="group" aria-label="Elige una opción para ensayar">
          {activity.options.map((option, index) => (
            <button key={option} type="button" aria-pressed={notes.activityChoice === index} onClick={() => update({ activityChoice: index })}
              className={`w-full rounded-xl border-2 p-3 text-left text-sm ${notes.activityChoice === index ? "border-[var(--sage-deep)] bg-white" : "border-[#eadfce] hover:bg-white"}`}>{option}</button>
          ))}
          {Number.isInteger(notes.activityChoice) && <p role="status" className="rounded-xl bg-white p-3 text-sm">Ahora practica formulando esa elección como una pregunta directa, sin dar por hecho la respuesta.</p>}
        </div>
      )}

      {activity.mode === "conversation" && (
        <div className="mt-4 rounded-xl bg-white p-4">
          <p className="text-lg">{day.question}</p>
          <a data-testid="share-question-button" href={`https://wa.me/?text=${encodeURIComponent(`Para hablar hoy: ${day.question}`)}`} target="_blank" rel="noreferrer" className="btn-ghost mt-3 !py-2 text-sm"><Send size={16} /> Compartir pregunta</a>
        </div>
      )}

      {["practice", "checklist"].includes(activity.mode) && (
        <div className="mt-4 space-y-2">
          {day.exercise.map((step, index) => (
            <button key={step} type="button" data-testid={`exercise-step-${index}`} onClick={() => onTick(index)} aria-pressed={ticks.includes(index)}
              className={`w-full rounded-xl border-2 p-3 text-left text-sm flex gap-3 ${ticks.includes(index) ? "bg-white border-[var(--sage-deep)]" : "border-[#eadfce] hover:bg-white"}`}>
              <span className="shrink-0 w-5 h-5 rounded-full border-2 grid place-items-center">{ticks.includes(index) && <Check size={13} />}</span>{step}
            </button>
          ))}
          <p className="text-xs text-[#7a6c5e]" data-testid="exercise-progress">{ticks.length} de {day.exercise.length} pasos</p>
        </div>
      )}

      <p className="mt-3 text-xs text-[#7a6c5e]">Tus respuestas se guardan solo en este dispositivo.</p>
    </section>
  );
}